<template>
  <AdminLayout>
    <v-divider class="mb-4" />

    <div class="d-flex align-center mb-6">
      <v-btn
        variant="outlined"
        size="small"
        color="primary"
        @click="$router.push('/admin/buchungen')"
      >
        <v-icon start size="small">mdi-arrow-left</v-icon>
        Zurück
      </v-btn>
    </div>

    <v-row>
      <v-col cols="12" md="4">
        <v-list-item>
          
            <v-list-item-title class="font-weight-bold"> ID </v-list-item-title>
            <v-list-item-subtitle>
              {{ api.recurringBookingDetails?.id }}
            </v-list-item-subtitle>
          
        </v-list-item>
      </v-col>

      <v-col cols="12" md="4">
        <v-list-item>
          
            <v-list-item-title class="font-weight-bold">
              Wiederholung
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ recurrenceText }}
            </v-list-item-subtitle>
          
        </v-list-item>
      </v-col>

      <v-col cols="12" md="4">
        <v-list-item>
          
            <v-list-item-title class="font-weight-bold">
              Gültig bis
            </v-list-item-title>
            <v-list-item-subtitle>
              <span v-if="api.recurringBookingDetails?.repeatUntil">
                {{
                  new Date(
                    api.recurringBookingDetails?.repeatUntil
                  ).toLocaleDateString("de-DE")
                }}
              </span>
            </v-list-item-subtitle>
          
        </v-list-item>
      </v-col>
    </v-row>
    <div class="page-header mt-3">
      <v-text-field
        v-model="searchTerm"
        label="Buchung suchen..."
        append-inner-icon="mdi-magnify"
        variant="solo"
        clearable
        class="search-field"
      ></v-text-field>
    </div>

    <div v-if="selectedBookingIds.length" class="d-flex justify-end mb-3">
      <v-btn
        :loading="isCommitLoading"
        color="success"
        class="mr-2"
        :disabled="disableApprove"
        @click="commitSelectedBookings"
      >
        <v-icon start size="small">mdi-check</v-icon> Ausgewählte Buchungen freigeben
      </v-btn>
      <v-btn
        :loading="isRejectLoading"
        color="error"
        :disabled="disableReject"
        @click="rejectSelectedBookings"
      >
        <v-icon start size="small">mdi-close</v-icon> {{ showCancelButtonLabel }}
      </v-btn>
    </div>

    <div class="page-content">
      <v-skeleton-loader type="table" class="flex">
        <BookingTable
          type="recurring"
          :bookings="filteredBookings"
          :loading="loading"
          :reset-trigger="resetTrigger"
          @open-booking="onOpenBooking"
          @open-edit-booking="onOpenEditBooking"
          @commit-booking="commitBooking"
          @open-delete-dialog="onOpenDeleteDialog"
          @reject-booking="onOpenRejectDialog"
          @update:selected-ids="onSelectedIds"
        />
      </v-skeleton-loader>
    </div>

    <BookingEdit
      :booking="selectedBooking"
      :open="openEditDialog"
      :bookables="bookables"
      :workflow="workflow"
      @close="onCloseEditDialog"
    />
    <BookingDeleteConformationDialog
      :to-delete="selectedBooking"
      :open="openDeleteDialog"
      @close="onCloseDeleteDialog"
    />
    <BookingRejectConformationDialog
      :to-reject="selectedBooking"
      :open="openRejectDialog"
      @close="onCloseRejectDialog"
    />
    <v-dialog v-model="openBookingDialog" max-width="800px">
      <BookingDetails
        :booking="selectedBooking"
        @update="updateBooking"
        @close="onCloseBookingDialog"
      ></BookingDetails>
    </v-dialog>
  </AdminLayout>
</template>

<script>
import Fuse from "fuse.js";
import AdminLayout from "@/layouts/Admin.vue";
import { mapActions, mapGetters } from "vuex";
import ApiBookingService from "@/services/api/ApiBookingService";
import BookingEdit from "@/components/Booking/BookingEdit.vue";
import BookingDeleteConformationDialog from "@/components/Booking/BookingDeleteConformationDialog.vue";
import BookingRejectConformationDialog from "@/components/Booking/BookingRejectConformationDialog.vue";
import ApiBookablesService from "@/services/api/ApiBookablesService";
import BookingPermissionService from "@/services/permissions/BookingPermissionService";
import BookingDetails from "@/components/Booking/BookingDetails.vue";
import BookingTable from "@/components/Booking/BookingTable.vue";
import ApiWorkflowService from "@/services/api/ApiWorkflowService";
import Matomo from "@/utils/Matomo";

export default {
  components: {
    BookingTable,
    BookingDetails,
    BookingDeleteConformationDialog,
    BookingRejectConformationDialog,
    AdminLayout,
    BookingEdit,
  },
  data() {
    return {
      showBacklog: false,
      fuse: null,
      reason: "",
      value: "",
      searchTerm: "",
      api: {
        bookings: [],
        recurringBookingDetails: {},
      },
      selectedBookingIds: [],
      openEditDialog: false,
      openDeleteDialog: false,
      openRejectDialog: false,
      selectedBooking: {},
      isCommitLoading: false,
      isRejectLoading: false,
      bookables: [],
      openBookingDialog: false,
      openRecurringBookingDialog: false,
      currentView: "list",
      resetTrigger: 0,
      workflow: {},
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
      tenantId: "tenants/currentTenantId",
    }),
    BookingPermissionService() {
      return BookingPermissionService;
    },
    filteredBookings() {
      if (!this.searchTerm) {
        return this.api.bookings || [];
      }
      const terms = this.searchTerm.trim().split(/\s+/);
      const searchQuery = {
        $and: terms.map((term) => ({
          $or: [
            { id: `'${term}` },
            { mail: `'${term}` },
            { comment: `'${term}` },
            { name: `'${term}` },
            { street: `'${term}` },
            { zipCode: `'${term}` },
            { location: `'${term}` },
            { company: `'${term}` },
            { phone: `'${term}` },
            { "bookableItems.bookableId": `'${term}` },
            { "bookableItems._bookableUsed.id": `'${term}` },
            { "bookableItems._bookableUsed.title": `'${term}` },
            { "bookableItems._bookableUsed.description": `'${term}` },
            { "bookableItems._bookableUsed.type": `'${term}` },
            { "bookableItems._bookableUsed.eventId": `'${term}` },
            { "bookableItems._bookableUsed.priceEur": `'${term}` },
            { "bookableItems._bookableUsed.attachments.id": `'${term}` },
            { "bookableItems._bookableUsed.attachments.type": `'${term}` },
            { "bookableItems._bookableUsed.attachments.title": `'${term}` },
            { "bookableItems._bookableUsed.attachments.url": `'${term}` },
            { "_populated.bookable.flags": `'${term}` },
            { "_populated.bookable.tags": `'${term}` },
            { "_populated.bookable.bookingNotes": `'${term}` },
          ],
        })),
      };

      const results = this.fuse.search(searchQuery);
      return results.map((result) => result.item);
    },
    showCancelButtonLabel() {
      if (!this.selectedBookingIds.length) return "";
      const selectedBookings = this.api.bookings.filter((b) =>
        this.selectedBookingIds.includes(b.id)
      );

      const allCommitted = selectedBookings.every((b) => b.isCommitted);
      const allRejectable = selectedBookings.every((b) => !b.isCommitted);

      if (allCommitted) return "Ausgewählte Buchungen stornieren";
      if (allRejectable) return "Ausgewählte Buchungen ablehnen";
      return "Ausgewählte Buchungen stornieren/ablehnen";
    },
    disableApprove() {
      if (!this.selectedBookingIds.length) return true;
      return this.api.bookings
        .filter((b) => this.selectedBookingIds.includes(b.id))
        .some((b) => b.isCommitted === true && b.isRejected === false);
    },
    disableReject() {
      if (!this.selectedBookingIds.length) return true;
      return this.api.bookings
        .filter((b) => this.selectedBookingIds.includes(b.id))
        .some((b) => b.isRejected === true);
    },
    recurrenceText() {
      const info = this.api.recurringBookingDetails;
      if (!info || !info.repeatUnit) return "-";
      const { repeatUnit, repeatEvery, repeatOnWeekdays, monthRepeatBy } = info;
      switch (repeatUnit) {
      case "day":
        return `Alle ${repeatEvery} ${repeatEvery === 1 ? "Tag" : "Tage"}`;
      case "week":
        if (repeatOnWeekdays?.length) {
          const weekdays = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
          const days = repeatOnWeekdays.map((d) => weekdays[d]).join(", ");
          return `Alle ${repeatEvery} ${
            repeatEvery === 1 ? "Woche" : "Wochen"
          } am ${days}`;
        }
        return `Alle ${repeatEvery} ${
          repeatEvery === 1 ? "Woche" : "Wochen"
        }`;
      case "month":
        if (monthRepeatBy === "date") {
          return `Alle ${repeatEvery} ${
            repeatEvery === 1 ? "Monat" : "Monate"
          } am ${new Date(info.firstTimeBegin).getDate()}.`;
        } else if (monthRepeatBy === "weekday") {
          const weekdays = [
            "Sonntag",
            "Montag",
            "Dienstag",
            "Mittwoch",
            "Donnerstag",
            "Freitag",
            "Samstag",
          ];
          const date = new Date(info.firstTimeBegin);
          const weekNumber = Math.ceil(date.getDate() / 7);
          const dayName = weekdays[date.getDay()];
          return `Alle ${repeatEvery} ${
            repeatEvery === 1 ? "Monat" : "Monate"
          } am ${weekNumber}. ${dayName}`;
        }
        return `Alle ${repeatEvery} ${
          repeatEvery === 1 ? "Monat" : "Monate"
        }`;

      default:
        return "-";
      }
    },
  },
  watch: {
    tenantId() {
      this.fetchRecurringBookings();
      this.fetchBookables();
    },
  },
  methods: {
    ...mapActions({
      addToast: "toasts/add",
      startLoading: "loading/start",
      stopLoading: "loading/stop",
    }),
    onSelectedIds(ids) {
      this.selectedBookingIds = ids;
    },
    resetBookingSelection() {
      this.resetTrigger++;
    },
    //create customSearch to get title of bookable by id
    customSearch(value, search) {
      // return bookables id of this.bookables if they include search string in title
      const bookableIds = this.bookables
        .filter((bookable) =>
          bookable.title.toLowerCase().includes(search.toLowerCase())
        )
        .map((bookable) => bookable.id);
      // return value if value is one of the bookables id
      if (bookableIds.includes(value?.toString())) {
        return true;
      } else if (
        value?.toString().toLowerCase().includes(search.toLowerCase())
      ) {
        return true;
      } else if (typeof value === "object" && value?.length > 0) {
        // for key in value check if value[key] is one of the bookables id
        for (const key in value) {
          if (bookableIds.includes(value[key]?.toString())) {
            return true;
          }
        }
      }
    },

    fetchBookables() {
      ApiBookablesService.getBookables()
        .then((response) => {
          this.bookables = response.data;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async fetchRecurringBookings() {
      await this.startLoading("fetch-recurring-bookings");

      await ApiBookingService.getRecurringBookings(
        undefined,
        this.$route.params.id,
        true
      )
        .then((response) => {
          this.api.recurringBookingDetails =
            response.data?.recurringBookingDetails;
          this.api.bookings = response.data?.bookings;
        })
        .finally(() => {
          this.stopLoading("fetch-recurring-bookings");
          this.initializeFuse();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    commitBooking(id) {
      ApiBookingService.commitBooking(id)
        .then((response) => {
          if (response.status === 200) {
            this.fetchRecurringBookings();
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    commitSelectedBookings() {
      this.isCommitLoading = true;
      ApiBookingService.commitSelectedBookings(this.selectedBookingIds)
        .then((response) => {
          if (response.status === 200) {
            this.fetchRecurringBookings();
            this.selectedBookingIds = [];
            this.resetBookingSelection();
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => (this.isCommitLoading = false));
    },
    rejectSelectedBookings() {
      this.isRejectLoading = true;
      ApiBookingService.rejectSelectedBookings(
        this.selectedBookingIds,
        this.reason
      )
        .then((response) => {
          if (response.status === 200) {
            this.fetchRecurringBookings();
            Matomo.trackEvent([
              "trackEvent",
              "Booking",
              "Rejected (Bulk)",
              `${this.selectedBookingIds?.join(", ")}`,
              this.selectedBookingIds?.length,
            ]);
            this.selectedBookingIds = [];
            this.resetBookingSelection();
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => (this.isRejectLoading = false));
    },
    onOpenBooking(bookingId) {
      this.selectedBooking = Object.assign(
        {},
        this.api.bookings.find((booking) => booking.id === bookingId)
      );
      this.openBookingDialog = true;
    },
    onOpenEditBooking(bookingId) {
      this.selectedBooking = Object.assign(
        {},
        this.api.bookings.find((booking) => booking.id === bookingId)
      );
      this.openEditDialog = true;
    },
    onOpenDeleteDialog(bookingId) {
      this.selectedBooking = Object.assign(
        {},
        this.api.bookings.find((booking) => booking.id === bookingId)
      );
      this.openDeleteDialog = true;
    },
    onOpenRejectDialog(bookingId) {
      this.selectedBooking = Object.assign(
        {},
        this.api.bookings.find((booking) => booking.id === bookingId)
      );
      this.openRejectDialog = true;
    },
    onCloseEditDialog() {
      this.fetchRecurringBookings();
      this.openEditDialog = false;
    },
    onCloseDeleteDialog() {
      this.fetchRecurringBookings();
      this.openDeleteDialog = false;
    },
    onCloseRejectDialog() {
      this.fetchRecurringBookings();
      this.openRejectDialog = false;
    },
    onCloseBookingDialog() {
      this.openBookingDialog = false;
    },
    async updateBooking(bookingId) {
      await this.fetchRecurringBookings();
      this.selectedBooking = Object.assign(
        {},
        this.api.bookings.find((booking) => booking.id === bookingId)
      );
    },
    initializeFuse() {
      const options = {
        includeScore: true,
        threshold: 0.3,
        useExtendedSearch: true,
        keys: [
          "id",
          "mail",
          "comment",
          "name",
          "street",
          "zipCode",
          "location",
          "company",
          "phone",

          "bookableItems.bookableId",
          "bookableItems._bookableUsed.id",
          "bookableItems._bookableUsed.title",
          "bookableItems._bookableUsed.type",
          "bookableItems._bookableUsed.eventId",
          "bookableItems._bookableUsed.priceEur",

          "bookableItems._bookableUsed.attachments.id",
          "bookableItems._bookableUsed.attachments.type",
          "bookableItems._bookableUsed.attachments.title",
          "bookableItems._bookableUsed.attachments.url",

          "_populated.bookable.flags",
          "_populated.bookable.tags",
          "_populated.bookable.bookingNotes",
        ],
      };
      this.fuse = new Fuse(this.api.bookings, options);
    },
    async fetchWorkflow() {
      this.workflow = await ApiWorkflowService.getWorkflowStates();
    },
  },
  created() {
    this.fetchRecurringBookings();
    this.fetchBookables();
    this.fetchWorkflow();
  },
};
</script>

<style scoped lang="scss">
.search-field {
  border-radius: 15px;
}
::v-deep .active-button {
  color: black !important;
  background-color: var(--v-secondary-base) !important;
}

html,
body {
  height: 100%;
  margin: 0;
}

.page-container {
  /* page-container selbst muss nicht zwingend Flex sein,
     wenn du in AdminLayout schon Flex-Logik nutzt.
     Kann aber, wenn man Header/Footer explizit abgrenzen möchte. */
  display: flex;
  flex-direction: column;
  /* Hier KEIN fixed height nötig, wir vererben die "Höhe" vom AdminLayout her. */
}

.page-header {
  /* Nimmt nur so viel Platz, wie nötig */
  flex: 0 0 auto;
}

.page-content {
  /* Hier könnte ebenfalls overflow, wenn du den inneren Bereich nochmals eigenständig scrollen lassen willst.
     Falls das jedoch global im AdminLayout schon geregelt wird, kannst du es hier auch simpler halten. */
  flex: 1 1 auto;
  overflow-y: auto;
  padding-bottom: 24px;
}

.page-footer {
  flex: 0 0 auto;
}
</style>
